"use client";

import { useTranslations } from "next-intl";

const CORE_PARAMS = [
	{ label: "coreParamsDimensionalAccuracy", value: "coreParamsDimensionalAccuracyValue" },
	{ label: "coreParamsSurfaceFinish", value: "coreParamsSurfaceFinishValue" },
	{ label: "coreParamsMinWallThickness", value: "coreParamsMinWallThicknessValue" },
	{ label: "coreParamsFillingPressure", value: "coreParamsFillingPressureValue" },
	{ label: "coreParamsFillingSpeed", value: "coreParamsFillingSpeedValue" },
	{ label: "coreParamsSingleMoldCycle", value: "coreParamsSingleMoldCycleValue" },
	{ label: "coreParamsWeightRange", value: "coreParamsWeightRangeValue" },
	{ label: "coreParamsBatchCapacity", value: "coreParamsBatchCapacityValue" },
] as const;

const TABLE_HEADERS = [
	"coreParamsTableHeaderMaterial",
	"coreParamsTableHeaderMeltingTemp",
	"coreParamsTableHeaderTensileStrength",
	"coreParamsTableHeaderHardness",
	"coreParamsTableHeaderProcess",
	"coreParamsTableHeaderApplication",
] as const;

const ALLOY_ROWS = [
	{
		name: "coreParamsAlloy1Name",
		meltingTemp: "coreParamsAlloy1MeltingTemp",
		tensileStrength: "coreParamsAlloy1TensileStrength",
		hardness: "coreParamsAlloy1Hardness",
		process: "coreParamsAlloy1Process",
		application: "coreParamsAlloy1Application",
	},
	{
		name: "coreParamsAlloy2Name",
		meltingTemp: "coreParamsAlloy2MeltingTemp",
		tensileStrength: "coreParamsAlloy2TensileStrength",
		hardness: "coreParamsAlloy2Hardness",
		process: "coreParamsAlloy2Process",
		application: "coreParamsAlloy2Application",
	},
	{
		name: "coreParamsAlloy3Name",
		meltingTemp: "coreParamsAlloy3MeltingTemp",
		tensileStrength: "coreParamsAlloy3TensileStrength",
		hardness: "coreParamsAlloy3Hardness",
		process: "coreParamsAlloy3Process",
		application: "coreParamsAlloy3Application",
	},
	{
		name: "coreParamsAlloy4Name",
		meltingTemp: "coreParamsAlloy4MeltingTemp",
		tensileStrength: "coreParamsAlloy4TensileStrength",
		hardness: "coreParamsAlloy4Hardness",
		process: "coreParamsAlloy4Process",
		application: "coreParamsAlloy4Application",
	},
] as const;

export default function CoreTechnicalParameters() {
	const t = useTranslations("new-high-pressure-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("coreParamsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("coreParamsDesc")}</p>
				</header>

				{/* Core Parameters Section */}
				<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
					{t("coreParamsSectionTitle")}
				</h3>
				<div className="mb-8 grid grid-cols-2 gap-x-4 gap-y-6 sm:mb-10 md:mb-12 md:grid-cols-4 md:gap-x-6 lg:mb-14">
					{CORE_PARAMS.map((param) => (
						<div key={param.label} className="text-center">
							<p className="text-xs text-gray-900 sm:text-sm">{t(param.label)}</p>
							<p className="mt-1 text-sm font-bold text-black sm:text-base md:text-lg">{t(param.value)}</p>
						</div>
					))}
				</div>

				{/* Alloy-Specific Process Parameters Table */}
				<h3 className="mb-6 text-center text-lg font-bold text-black sm:mb-8 sm:text-xl md:text-2xl">
					{t("coreParamsAlloySectionTitle")}
				</h3>
				<div className="overflow-x-auto">
					<table className="w-full min-w-[800px] border-collapse">
						<thead>
							<tr className="bg-[#64a525] text-white">
								{TABLE_HEADERS.map((header) => (
									<th
										key={header}
										className="px-4 py-3 text-left text-xs font-bold sm:text-sm md:px-5 md:py-4"
									>
										{t(header)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{ALLOY_ROWS.map((row, index) => (
								<tr key={row.name} className={`border-b border-gray-200`}>
									<td className="px-4 py-4 text-xs font-bold text-black sm:text-sm md:px-5 md:py-5">
										{t(row.name)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.meltingTemp)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.tensileStrength)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.hardness)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.process)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.application)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
